import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	inject,
	Input,
	Output,
	signal,
	WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LabelsService } from '@app/shared/services/labels.service';
import { LabelBadgeComponent } from '@app/pages/settings/labels/label-item-row/label-badge/label-badge.component';
import { LabelQuickSearchPipe } from '@app/pages/settings/labels/pipes/label-quick-search.pipe';
import { AuNativeInputModule } from '@shared/ui-components/au-native-input/au-native-input.module';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ILabel } from '@app/pages/settings/interfaces/labels.interfaces';
import { decrementIndex, incrementIndex } from '@app/core/utils/list-navigation-utils';
import { NgxTolgeeModule } from '@tolgee/ngx';

@Component({
	selector: 'label-list-menu',
	imports: [
		CommonModule,
		LabelBadgeComponent,
		MatCheckboxModule,
		AuNativeInputModule,
		NgxTolgeeModule,
		LabelQuickSearchPipe,
	],
	templateUrl: './label-list-menu.component.html',
	styleUrls: ['./label-list-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabelListMenuComponent {
	private readonly LabelsService = inject(LabelsService);
	private readonly el = inject(ElementRef);

	searchText: WritableSignal<string> = signal('');

	@Input() openedFromActionCenter: boolean = false;
	@Input() selectedLabelPks: string[] = [];
	@Output() selectedLabelsChange = new EventEmitter<{
		pks: string[];
		labels: ILabel[];
	}>();

	labels = this.LabelsService.labels;
	index: WritableSignal<number> = signal(0);
	isChecked(label: Partial<ILabel>) {
		if (label.pk) {
			return this.selectedLabelPks.includes(label.pk);
		}
		return false;
	}

	toggleSelection(checked: boolean, label: Partial<ILabel>) {
		let selectedPks = [];
		if (checked) {
			selectedPks = [...this.selectedLabelPks, label.pk];
		} else {
			selectedPks = this.selectedLabelPks.filter(pk => pk !== label.pk);
		}
		const selectedLabels: ILabel[] = this.labels().filter(label =>
			selectedPks.includes(label.pk)
		);
		this.selectedLabelsChange.emit({ pks: selectedPks, labels: selectedLabels });
	}

	@HostListener('window:keydown', ['$event'])
	keyboardNav(event: KeyboardEvent) {
		if (this.openedFromActionCenter) {
			if (event.key === 'ArrowUp') {
				this.index.set(decrementIndex(this.index(), this.labels().length));
			}
			if (event.key === 'ArrowDown') {
				this.index.set(incrementIndex(this.index(), this.labels().length));
			}
			setTimeout(() => {
				this.scrollToActiveElement();
			}, 100);
			if (event.key === ' ') {
				this.toggleSelection(
					!this.isChecked(this.labels()[this.index()]),
					this.labels()[this.index()]
				);
			}
		}
	}

	scrollToActiveElement() {
		const activeElement = this.el.nativeElement.querySelector('.active');

		if (activeElement) {
			activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}
}
